import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Button } from '../common';

const PipelineStagesSettings = () => {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggingId, setDraggingId] = useState(null);
  const [newStage, setNewStage] = useState({
    name: '',
    color: '#3498db',
    is_won: false,
    is_lost: false
  });
  const [editingStage, setEditingStage] = useState(null);
  const [reordering, setReordering] = useState(false);

  // Available colors
  const colorOptions = [
    { name: 'Blue', value: '#3498db' },
    { name: 'Green', value: '#2ecc71' },
    { name: 'Purple', value: '#9b59b6' },
    { name: 'Red', value: '#e74c3c' },
    { name: 'Orange', value: '#e67e22' },
    { name: 'Teal', value: '#1abc9c' },
    { name: 'Yellow', value: '#f1c40f' },
    { name: 'Gray', value: '#95a5a6' }
  ];

  // Fetch stages on component mount
  useEffect(() => {
    fetchStages();
  }, []);

  const fetchStages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/deals/stages/all', {
        headers: { 'x-auth-token': token }
      });
      setStages(res.data);
    } catch (err) {
      toast.error('Failed to load pipeline stages');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStage = async () => {
    try {
      if (!newStage.name.trim()) {
        toast.warn('Stage name is required');
        return;
      }

      const token = localStorage.getItem('token');
      const res = await axios.post('/api/deals/stages', {
        ...newStage,
        display_order: stages.length + 1,
      }, {
        headers: { 'x-auth-token': token }
      });

      setStages([...stages, res.data]);
      setNewStage({
        name: '',
        color: '#3498db',
        is_won: false,
        is_lost: false
      });
      toast.success('Stage added successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add stage');
      console.error(err);
    }
  };

  const handleUpdateStage = async (id, updates) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`/api/deals/stages/${id}`, updates, {
        headers: { 'x-auth-token': token }
      });

      setStages(stages.map(stage => 
        stage.id === id ? res.data : stage
      ));
      toast.success('Stage updated');
      setEditingStage(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update stage');
      console.error(err);
    }
  };

  const handleDeleteStage = async (id) => {
    try {
      if (!window.confirm('Are you sure you want to delete this stage? This cannot be undone.')) {
        return;
      }

      const token = localStorage.getItem('token');
      await axios.delete(`/api/deals/stages/${id}`, {
        headers: { 'x-auth-token': token }
      });

      setStages(stages.filter(stage => stage.id !== id));
      toast.success('Stage deleted');
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.dealsCount) {
        toast.error(`Cannot delete stage with ${err.response.data.dealsCount} deals. Move the deals first.`);
      } else {
        toast.error(err.response?.data?.message || 'Failed to delete stage');
      }
      console.error(err);
    }
  };

  const handleDragStart = (id) => {
    setDraggingId(id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (targetId) => {
    if (draggingId === targetId) {
      setDraggingId(null);
      return;
    }

    const draggedIndex = stages.findIndex(stage => stage.id === draggingId);
    const targetIndex = stages.findIndex(stage => stage.id === targetId);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggingId(null);
      return;
    }

    // Create a new array with the reordered stages
    const newStages = [...stages];
    const [draggedStage] = newStages.splice(draggedIndex, 1);
    newStages.splice(targetIndex, 0, draggedStage);

    // Update local state for immediate UI feedback
    setStages(newStages);
    setDraggingId(null);

    // Save the new order to the server
    saveStageOrder(newStages.map(stage => stage.id));
  };

  const saveStageOrder = async (stageIds) => {
    try {
      setReordering(true);
      const token = localStorage.getItem('token');
      const res = await axios.post('/api/deals/stages/reorder', {
        stageIds
      }, {
        headers: { 'x-auth-token': token }
      });

      setStages(res.data);
      toast.success('Stages reordered successfully');
    } catch (err) {
      toast.error('Failed to reorder stages');
      console.error(err);
      fetchStages(); // Reload original order on error
    } finally {
      setReordering(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-semibold mb-4">Pipeline Stages</h2>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-10 bg-gray-200 rounded mb-2"></div>
          <div className="h-10 bg-gray-200 rounded mb-2"></div>
          <div className="h-10 bg-gray-200 rounded mb-2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-4">Pipeline Stages</h2>
      
      <div className="mb-8">
        <p className="text-gray-600 mb-4">
          Configure the stages that deals move through in your pipeline. Drag stages to reorder them.
        </p>
        
        {/* Stage List */}
        <div className="border rounded-md overflow-hidden mb-8">
          <div className="bg-gray-50 px-4 py-2 border-b grid grid-cols-12 gap-2">
            <div className="col-span-4 font-medium">Name</div>
            <div className="col-span-2 font-medium">Color</div>
            <div className="col-span-4 font-medium">Properties</div>
            <div className="col-span-2 font-medium text-right">Actions</div>
          </div>
          
          {stages.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No pipeline stages defined yet. Add your first stage below.
            </div>
          ) : (
            <div className="divide-y">
              {stages.map(stage => (
                <div
                  key={stage.id}
                  className={`px-4 py-3 grid grid-cols-12 gap-2 items-center ${
                    draggingId === stage.id ? 'bg-blue-50' : 'bg-white'
                  } cursor-move`}
                  draggable
                  onDragStart={() => handleDragStart(stage.id)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(stage.id)}
                >
                  {editingStage?.id === stage.id ? (
                    // Editing mode
                    <>
                      <div className="col-span-4">
                        <input
                          type="text"
                          className="w-full px-2 py-1 border rounded"
                          value={editingStage.name}
                          onChange={(e) => setEditingStage({...editingStage, name: e.target.value})}
                        />
                      </div>
                      <div className="col-span-2">
                        <select
                          className="w-full px-2 py-1 border rounded"
                          value={editingStage.color || '#3498db'}
                          onChange={(e) => setEditingStage({...editingStage, color: e.target.value})}
                          style={{ backgroundColor: editingStage.color }}
                        >
                          {colorOptions.map(color => (
                            <option key={color.value} value={color.value}>{color.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-4 flex space-x-4">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            className="mr-1"
                            checked={editingStage.is_won}
                            onChange={(e) => setEditingStage({...editingStage, is_won: e.target.checked})}
                          />
                          <span className="text-sm">Won</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            className="mr-1"
                            checked={editingStage.is_lost}
                            onChange={(e) => setEditingStage({...editingStage, is_lost: e.target.checked})}
                          />
                          <span className="text-sm">Lost</span>
                        </label>
                      </div>
                      <div className="col-span-2 flex justify-end space-x-1">
                        <button
                          className="p-1 text-green-600 hover:text-green-800"
                          onClick={() => handleUpdateStage(stage.id, editingStage)}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                        </button>
                        <button
                          className="p-1 text-gray-600 hover:text-gray-800"
                          onClick={() => setEditingStage(null)}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                          </svg>
                        </button>
                      </div>
                    </>
                  ) : (
                    // View mode
                    <>
                      <div className="col-span-4 font-medium">{stage.name}</div>
                      <div className="col-span-2">
                        <div
                          className="w-6 h-6 rounded"
                          style={{ backgroundColor: stage.color || '#3498db' }}
                        ></div>
                      </div>
                      <div className="col-span-4">
                        {stage.is_won && <span className="inline-block px-2 py-1 mr-2 bg-green-100 text-green-800 text-xs rounded">Won</span>}
                        {stage.is_lost && <span className="inline-block px-2 py-1 bg-red-100 text-red-800 text-xs rounded">Lost</span>}
                      </div>
                      <div className="col-span-2 flex justify-end space-x-1">
                        <button
                          className="p-1 text-blue-600 hover:text-blue-800"
                          onClick={() => setEditingStage({...stage})}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
                          </svg>
                        </button>
                        <button
                          className="p-1 text-red-600 hover:text-red-800"
                          onClick={() => handleDeleteStage(stage.id)}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                          </svg>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Add New Stage Form */}
        <div className="bg-gray-50 p-4 border rounded-md">
          <h3 className="text-lg font-medium mb-4">Add New Stage</h3>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-md"
                placeholder="Stage name"
                value={newStage.name}
                onChange={(e) => setNewStage({...newStage, name: e.target.value})}
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
              <select
                className="w-full px-3 py-2 border rounded-md"
                value={newStage.color}
                onChange={(e) => setNewStage({...newStage, color: e.target.value})}
                style={{ backgroundColor: newStage.color }}
              >
                {colorOptions.map(color => (
                  <option key={color.value} value={color.value}>{color.name}</option>
                ))}
              </select>
            </div>
            
            <div className="md:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Properties</label>
              <div className="flex space-x-4 h-full items-center">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="mr-1"
                    checked={newStage.is_won}
                    onChange={(e) => setNewStage({...newStage, is_won: e.target.checked, is_lost: e.target.checked ? false : newStage.is_lost})}
                  />
                  <span className="text-sm">Won</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="mr-1"
                    checked={newStage.is_lost}
                    onChange={(e) => setNewStage({...newStage, is_lost: e.target.checked, is_won: e.target.checked ? false : newStage.is_won})}
                  />
                  <span className="text-sm">Lost</span>
                </label>
              </div>
            </div>
            
            <div className="md:col-span-2 flex items-end">
              <Button
                onClick={handleAddStage}
                className="w-full"
              >
                Add Stage
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PipelineStagesSettings;